import React from 'react';
import { Button, Input, Switch, Form, Space, Typography, Tooltip, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, GlobalOutlined, StarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { get as _get } from 'lodash';
import { ReceiverWebsite } from '../../../../types/receiver';

const { Text } = Typography;

type WebsiteManagerProps = {
  websites: ReceiverWebsite[];
  onChange: (websites: ReceiverWebsite[]) => void;
};

const WebsiteManager: React.FC<WebsiteManagerProps> = ({ websites, onChange }) => {
  const { t } = useTranslation();
  const { errors, touched } = useFormikContext();

  const addWebsite = () => {
    const newWebsite: ReceiverWebsite = {
      url: '',
      isPrimary: websites.length === 0,
    };
    onChange([...websites, newWebsite]);
  };

  const removeWebsite = (index: number) => {
    const updated = websites.filter((_, i) => i !== index);

    if (websites[index].isPrimary && updated.length > 0) {
      updated[0].isPrimary = true;
    }

    onChange(updated);
  };

  const updateWebsite = (index: number, field: keyof ReceiverWebsite, value: any) => {
    const updated = [...websites];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'isPrimary' && value) {
      updated.forEach((website, i) => {
        if (i !== index) {
          website.isPrimary = false;
        }
      });
    }

    onChange(updated);
  };

  const hasPrimary = websites.some(w => w.isPrimary);

  // Get validation errors for the websites array from Formik
  const websitesErrors = _get(errors, 'websites') as any;
  const websitesTouched = _get(touched, 'websites') as any;
  const generalError = typeof websitesErrors === 'string' ? websitesErrors : undefined;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Text strong>{t('companyProfile:websites')}</Text>
        <Button type="dashed" icon={<PlusOutlined />} onClick={addWebsite} size="small">
          {t('companyProfile:addWebsite')}
        </Button>
      </div>

      {generalError && <Alert message={generalError} type="error" showIcon />}

      {!hasPrimary && websites.length > 0 && !generalError && (
        <Alert message={t('companyProfile:noPrimaryWebsite')} type="warning" showIcon />
      )}

      <div className="space-y-3">
        {websites.map((website, index) => {
          const fieldError = websitesErrors?.[index]?.url;
          const fieldTouched = websitesTouched?.[index]?.url;
          const showError = fieldTouched && fieldError;

          return (
            <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-start space-x-3">
                <div className="flex-1">
                  <Form.Item
                    validateStatus={showError ? 'error' : ''}
                    help={showError ? fieldError : undefined}
                    className="mb-2"
                  >
                    <Input
                      prefix={<GlobalOutlined />}
                      placeholder={t('companyProfile:enterWebsiteUrl')}
                      value={website.url}
                      onChange={e => updateWebsite(index, 'url', e.target.value)}
                    />
                  </Form.Item>

                  <div className="flex items-center space-x-2">
                    <Switch
                      size="small"
                      checked={website.isPrimary}
                      onChange={checked => updateWebsite(index, 'isPrimary', checked)}
                    />
                    <Text type="secondary" className="text-sm">
                      {t('companyProfile:primaryWebsite')}
                    </Text>
                    {website.isPrimary && (
                      <Tooltip title={t('companyProfile:primaryWebsiteTooltip')}>
                        <StarOutlined className="text-yellow-500" />
                      </Tooltip>
                    )}
                  </div>
                </div>

                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => removeWebsite(index)}
                  className="text-red-500 hover:text-red-700"
                  size="small"
                />
              </div>
            </div>
          );
        })}
      </div>

      {websites.length === 0 && (
        <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
          <GlobalOutlined className="text-gray-400 text-2xl mb-2" />
          <Text type="secondary">{t('companyProfile:noWebsitesAdded')}</Text>
        </div>
      )}

      {websites.length > 1 && (
        <Alert message={t('companyProfile:multipleWebsitesInfo')} type="info" showIcon />
      )}
    </div>
  );
};

export default WebsiteManager;
